import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Distributor {
    code: string;
    name: string;
    areas?: string[];
}

interface ZoneDetailsProps {
    activeZone: string;
    zones: string[];
    onRenameZone: (oldName: string, newName: string) => void;
    onNavigate?: (path: string, data?: any) => void;
}

export const ZoneDetails: React.FC<ZoneDetailsProps> = ({
    activeZone,
    zones,
    onRenameZone,
    onNavigate
}) => {
    const navigate = useNavigate();

    // Sub-locations under each Zone (Warehouses / Distributors)
    const [zoneLocations, setZoneLocations] = useState<Record<string, string[]>>({
        Mumbai: [
            'Mumbai', 'Kalachowki', 'Worli', 'Kandivali', 'Kalyan', 'Malad',
            'Andheri east', 'Roha', 'Sakinaka', 'Ghatkopar', 'Mumbra', 'Malad',
            'Andheri west', 'Vashi', 'Vasai', 'Manchar'
        ],
        West: [
            'Pune', 'Kothrud', 'Hinjawadi', 'Hadapsar', 'Pimpri', 'Chinchwad',
            'Baner', 'Wakad', 'Shivajinagar', 'Aundh', 'Camp', 'Kalyan Nagar'
        ],
        South: [
            'Bangalore', 'Indiranagar', 'Jayanagar', 'Koramangala', 'Whitefield',
            'Chennai', 'Adyar', 'T. Nagar', 'Velachery', 'Hyderabad', 'Gachibowli'
        ],
        ROM: [
            'Nashik', 'College Road', 'Panchavati', 'Nagpur', 'Dharampeth',
            'Aurangabad', 'Cidco', 'Kolhapur', 'Rajarampuri', 'Solapur'
        ]
    });

    // Currently selected location pill (defaults to the first location in the active zone)
    const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

    // Reset selected location when activeZone changes
    useEffect(() => {
        queueMicrotask(() => setSelectedLocation(null));
    }, [activeZone]);

    // Distributors under each location
    const [distributors, setDistributors] = useState<Record<string, Distributor[]>>({
        Kalachowki: [
            { code: '6000888', name: 'Maurya Enterprises-Malad Distributor', areas: ['Malad West Beat', 'Orlem Beat'] },
            { code: '6000964', name: 'Nirmala Milk Centre-Jogeshwari Distributor', areas: ['Jogeshwari East Beat'] },
            { code: '6000581', name: 'Pratap Burman Distributor', areas: ['Goregaon Beat', 'Aarey Colony'] },
        ]
    });

    // Modals visibility state
    const [isEditZoneOpen, setIsEditZoneOpen] = useState(false);
    const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
    const [isViewDistributorOpen, setIsViewDistributorOpen] = useState(false);
    const [isEditDistributorOpen, setIsEditDistributorOpen] = useState(false);
    const [isAddAreaOpen, setIsAddAreaOpen] = useState(false);

    // Active items for detail/edit modals
    const [selectedDistributor, _setSelectedDistributor] = useState<Distributor | null>(null);

    // Form input states
    const [tempZoneName, setTempZoneName] = useState('');
    const [locationType, setLocationType] = useState<'Warehouse' | 'Distributor'>('Warehouse');
    const [newLocationName, setNewLocationName] = useState('');

    const [editDistName, setEditDistName] = useState('');
    const [editDistCode, setEditDistCode] = useState('');
    const [newAreaName, setNewAreaName] = useState('');

    // Custom toast notification state
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastType, setToastType] = useState<'success' | 'info'>('success');

    const showToast = (message: string, type: 'success' | 'info' = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setTimeout(() => setToastMessage(null), 3000);
    };

    if (!activeZone) return null;

    // Handlers for Edit Zone (currently unused as button is commented out)
    /* const handleOpenEditZone = () => {
        setTempZoneName(activeZone);
        setIsEditZoneOpen(true);
    }; */

    const handleEditZoneSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const oldName = activeZone;
        const newName = tempZoneName.trim();
        if (!newName || oldName === newName) {
            setIsEditZoneOpen(false);
            return;
        }
        if (zones.includes(newName)) {
            showToast('Zone already exists!', 'info');
            return;
        }

        // Notify parent to update its list and active state
        onRenameZone(oldName, newName);

        // Rename key in zoneLocations locally
        setZoneLocations(prev => {
            const copy = { ...prev };
            copy[newName] = copy[oldName] || [];
            delete copy[oldName];
            return copy;
        });

        setIsEditZoneOpen(false);
        showToast(`Zone name changed from "${oldName}" to "${newName}"!`);
    };

    // Handlers for Warehouse / Distributor Add
    /* const handleOpenAddWarehouse = () => {
        setLocationType('Warehouse');
        setNewLocationName('');
        setIsAddLocationOpen(true);
    }; */

    const handleOpenAddDistributor = () => {
        setLocationType('Distributor');
        setNewLocationName('');
        setIsAddLocationOpen(true);
    };

    const handleAddLocationSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const name = newLocationName.trim();
        if (!name) return;

        setZoneLocations(prev => {
            const currentList = prev[activeZone] || [];
            if (currentList.includes(name)) {
                showToast(`${locationType} "${name}" already exists!`, 'info');
                return prev;
            }
            return {
                ...prev,
                [activeZone]: [...currentList, name]
            };
        });

        setIsAddLocationOpen(false);
        showToast(`${locationType} "${name}" added to ${activeZone}!`);
    };

    // Download Active Zone Locations list
    /* const handleDownloadOutlets = () => {
        const locationsList = zoneLocations[activeZone] || [];
        if (locationsList.length === 0) {
            showToast(`No sub-locations found in ${activeZone} zone.`, 'info');
            return;
        }

        const headers = ['Location Name', 'Zone'];
        const csvRows = [headers.join(',')];

        locationsList.forEach(loc => {
            csvRows.push([`"${loc}"`, `"${activeZone}"`].join(','));
        });

        const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `gowardhan_locations_${activeZone.toLowerCase()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast(`Outlets list for "${activeZone}" downloaded!`);
    }; */

    // Download Distributors of the selected Location
    /* const handleDownloadLocationDistributors = () => {
        if (!selectedLocation) return;
        const distList = getDistributorsForLocation(selectedLocation);
        const headers = ['Code', 'Distributor Name', 'Areas'];
        const csvRows = [headers.join(',')];

        distList.forEach(d => {
            const areasStr = (d.areas || []).join(' | ');
            csvRows.push([`"${d.code}"`, `"${d.name}"`, `"${areasStr}"`].join(','));
        });

        const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `gowardhan_distributors_${selectedLocation.toLowerCase()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showToast(`Distributors list for "${selectedLocation}" downloaded!`);
    }; */

    const handleLocationClick = (loc: string) => {
        setSelectedLocation(loc);
        showToast(`Selected location: ${loc}`);
    };

    // Helper to fetch distributors (generates mock data for unconfigured locations dynamically)
    const getDistributorsForLocation = (locName: string): Distributor[] => {
        if (distributors[locName]) {
            return distributors[locName];
        }
        // Generate realistic fallbacks based on name
        return [
            { code: '6000101', name: `${locName} Dairy Agency`, areas: ['Sector 1 Beat', 'Main Market Beat'] },
            { code: '6000202', name: `Krishna Milk Point - ${locName}`, areas: ['Station Road Route'] },
            { code: '6000303', name: `${locName} Fresh Milk Agency`, areas: ['Highway Beat'] }
        ];
    };

    // Distributor Action Handlers
    const handleViewDistributor = (dist: Distributor) => {
        const data = {
            ...dist,
            location: selectedLocation || undefined,
            zone: activeZone
        };
        if (onNavigate) {
            onNavigate('/dashboard/view-distributor', data);
        } else {
            navigate('/dashboard/view-distributor', { state: { distributor: data } });
        }
    };

    /* const handleOpenEditDistributor = (dist: Distributor) => {
        setSelectedDistributor(dist);
        setEditDistName(dist.name);
        setEditDistCode(dist.code);
        setIsEditDistributorOpen(true);
    }; */

    const handleEditDistributorSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDistributor || !selectedLocation) return;
        const newName = editDistName.trim();
        const newCode = editDistCode.trim();
        if (!newName || !newCode) return;

        // Fetch current list
        const currentList = getDistributorsForLocation(selectedLocation);
        const updatedList = currentList.map(d =>
            d.code === selectedDistributor.code ? { ...d, name: newName, code: newCode } : d
        );

        setDistributors(prev => ({
            ...prev,
            [selectedLocation]: updatedList
        }));

        setIsEditDistributorOpen(false);
        showToast('Distributor updated successfully!');
    };

    /* const handleOpenAddArea = (dist: Distributor) => {
        setSelectedDistributor(dist);
        setNewAreaName('');
        setIsAddAreaOpen(true);
    }; */

    const handleAddAreaSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedDistributor || !selectedLocation) return;
        const area = newAreaName.trim();
        if (!area) return;

        const currentList = getDistributorsForLocation(selectedLocation);
        const updatedList = currentList.map(d => {
            if (d.code === selectedDistributor.code) {
                const currentAreas = d.areas || [];
                if (currentAreas.includes(area)) {
                    return d;
                }
                return { ...d, areas: [...currentAreas, area] };
            }
            return d;
        });

        setDistributors(prev => ({
            ...prev,
            [selectedLocation]: updatedList
        }));

        setIsAddAreaOpen(false);
        showToast(`Area "${area}" added to ${selectedDistributor.name}!`);
    };

    const locationsList = zoneLocations[activeZone] || [];
    const activeLocDistributors = selectedLocation ? getDistributorsForLocation(selectedLocation) : [];

    return (
        <>
            <div className="bg-white rounded-3xl shadow-sm border border-[#ECECF2] px-4 py-3">
                {/* Toast Notification */}
                {toastMessage && (
                    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 bg-slate-900 text-white text-xs font-bold rounded-xl shadow-lg border border-slate-800 animate-slideUp">
                        {toastType === 'success' ? (
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                        ) : (
                            <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
                        )}
                        {toastMessage}
                    </div>
                )}

                {/* Active Zone Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#828282] py-1">
                    <h5 className="text-xl font-bold text-[#1F1F1F] select-none m-0">
                        {activeZone}
                    </h5>

                    {/* Header Action Buttons matching mockup */}
                    <div className="flex flex-wrap items-center gap-6">
                    {/* <button
                        onClick={handleOpenAddWarehouse}
                        className="flex items-center gap-1 text-sm font-medium text-[#5D4037] transition-colors cursor-pointer"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
                            <path d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13412 13.9999 5.18208e-06 10.8659 0 7C3.22359e-06 3.13409 3.13412 0.000129983 7 0ZM7 0.976562C3.67356 0.976692 0.976566 3.67353 0.976562 7C0.976568 10.3265 3.67356 13.0233 7 13.0234C10.3265 13.0234 13.0234 10.3265 13.0234 7C13.0234 3.67345 10.3265 0.976562 7 0.976562ZM7 4.55859C7.26972 4.55859 7.48828 4.77715 7.48828 5.04688V6.51172H8.9541C9.22364 6.51194 9.44238 6.73041 9.44238 7C9.44236 7.26957 9.22362 7.48806 8.9541 7.48828H7.48828V8.95312C7.48828 9.22285 7.26972 9.44141 7 9.44141C6.73038 9.44129 6.51172 9.22278 6.51172 8.95312V7.48828H5.04688C4.77717 7.48828 4.55861 7.2697 4.55859 7C4.55859 6.73028 4.77715 6.51172 5.04688 6.51172H6.51172V5.04688C6.51172 4.77722 6.73038 4.55871 7 4.55859Z" fill="#5D4037" />
                        </svg>
                        Add Warehouse
                    </button> */}

                    <button
                        onClick={handleOpenAddDistributor}
                        className="flex items-center gap-1 text-sm font-medium text-[#5D4037] transition-colors cursor-pointer"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
                            <path d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13412 13.9999 5.18208e-06 10.8659 0 7C3.22359e-06 3.13409 3.13412 0.000129983 7 0ZM7 0.976562C3.67356 0.976692 0.976566 3.67353 0.976562 7C0.976568 10.3265 3.67356 13.0233 7 13.0234C10.3265 13.0234 13.0234 10.3265 13.0234 7C13.0234 3.67345 10.3265 0.976562 7 0.976562ZM7 4.55859C7.26972 4.55859 7.48828 4.77715 7.48828 5.04688V6.51172H8.9541C9.22364 6.51194 9.44238 6.73041 9.44238 7C9.44236 7.26957 9.22362 7.48806 8.9541 7.48828H7.48828V8.95312C7.48828 9.22285 7.26972 9.44141 7 9.44141C6.73038 9.44129 6.51172 9.22278 6.51172 8.95312V7.48828H5.04688C4.77717 7.48828 4.55861 7.2697 4.55859 7C4.55859 6.73028 4.77715 6.51172 5.04688 6.51172H6.51172V5.04688C6.51172 4.77722 6.73038 4.55871 7 4.55859Z" fill="#5D4037" />
                        </svg>
                        Add Distributor
                    </button>

                    {/* <button
                        onClick={handleOpenEditZone}
                        className="flex items-center gap-1 text-sm font-medium text-[#5D4037] transition-colors cursor-pointer"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M6.18165 1.5856C6.5332 1.5856 6.81833 1.87076 6.81837 2.22232C6.81837 2.57391 6.53323 2.85904 6.18165 2.85904H3.80665L3.54688 2.87271C2.267 3.00596 1.27066 4.08946 1.27345 5.40396L0.424814 5.40591H0.636728L1.27345 5.40396V10.1813L1.28614 10.4411C1.41345 11.7216 2.49208 12.7229 3.80665 12.7262H8.55665C9.8713 12.723/ 10.9508 11.7217 11.0781 10.4411L11.0908 10.1813V6.99673C11.091 6.64598 11.3761 6.36001 11.7275 6.36001C12.0789 6.36019 12.3641 6.64529 12.3643 6.99673V10.1784L12.3594 10.3747C12.2617 12.389 10.6 13.9945 8.56056 13.9997H3.80372L3.80567 13.3629V13.0036L3.80372 13.9997C1.69837 13.9945 -0.00449819 12.2837 8.92642e-06 10.1784V5.40591C-0.0040079 3.30093 1.69867 1.59069 3.80372 1.5856L3.80567 2.24185V2.22232L3.80372 1.5856H6.18165ZM9.35157 10.3395C9.70307 10.3396 9.98829 10.6246 9.98829 10.9762C9.98827 11.3277 9.70306 11.6129 9.35157 11.6129H3.01368C2.66211 11.6129 2.37698 11.3278 2.37696 10.9762C2.37696 10.6246 2.6621 10.3395 3.01368 10.3395H9.35157ZM9.53419 0.519192C9.93726 0.0123083 10.6353 -0.141641 11.209 0.138332L11.3242 0.201809L11.3887 0.246731L12.7178 1.30532L12.7578 1.3395C13.0145 1.58123 13.1591 1.91915 13.1572 2.27212C13.1555 2.59969 13.0258 2.91163 12.7998 3.14614L11.794 4.34927V4.35025L8.35645 8.44888L8.34864 8.45962C8.20163 8.62775 8.00509 8.74558 7.78712 8.79751L7.78028 8.79947L5.99806 9.20376C5.96256 9.21178 5.926 9.2165 5.88966 9.21841C5.32441 9.2465 4.83189 8.83531 4.7588 8.27407C4.75527 8.24692 4.75295 8.21943 4.75294 8.19204V6.36001C4.75295 6.34574 4.75392 6.33129 4.75489 6.31704C4.77065 6.08623 4.86321 5.86665 5.01856 5.69497L8.38673 1.88736L9.53321 0.517238L9.53419 0.519192ZM6.02638 6.47818V7.89126L7.4297 7.5729L10.0547 4.44204C9.76046 4.32154 9.50154 4.1496 9.28321 3.95962C9.08102 3.78371 8.90604 3.58663 8.75782 3.38833L6.02638 6.47818ZM10.6455 1.28189C10.6041 1.26468 10.5552 1.27565 10.5274 1.31216C10.5216 1.31965 10.5158 1.32741 10.5098 1.33456L9.62306 2.39224C9.74701 2.59728 9.91408 2.82025 10.1191 2.99868C10.3802 3.22582 10.6623 3.35355 10.9785 3.33853L11.8272 2.32388C11.8439 2.30382 11.8627 2.28508 11.8818 2.26724L10.6455 1.28189Z" fill="#5D4037" />
                        </svg>
                        Edit Zone
                    </button>

                    <button
                        onClick={handleDownloadOutlets}
                        className="flex items-center gap-1 text-sm font-medium text-[#5D4037] transition-colors cursor-pointer"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
                            <path d="M7.03711 0C8.54025 -6.51163e-06 9.71858 0.000452274 10.6377 0.124023C11.5783 0.250513 12.3207 0.514056 12.9033 1.09668C13.486 1.67934 13.7504 2.42157 13.877 3.3623C14.0005 4.28141 14 5.45977 14 6.96289V7.03711C14 8.54025 14.0005 9.71858 13.877 10.6377C13.7504 11.5784 13.486 12.3207 12.9033 12.9033C12.3207 13.486 11.5784 13.7504 10.6377 13.877C9.71858 14.0005 8.54025 14 7.03711 14H6.96289C5.45977 14 4.28141 14.0005 3.3623 13.877C2.42157 13.7504 1.67934 13.486 1.09668 12.9033C0.514056 12.3207 0.250513 11.5783 0.124023 10.6377C0.000452274 9.71858 -6.51163e-06 8.54025 0 7.03711V6.96289C-6.51163e-06 5.45977 0.000452274 4.28141 0.124023 3.3623C0.250514 2.42162 0.514048 1.67931 1.09668 1.09668C1.67931 0.514047 2.42163 0.250514 3.3623 0.124023C4.28141 0.000452274 5.45977 -6.51163e-06 6.96289 0H7.03711ZM7 0.976562C5.45139 0.976562 4.33869 0.977987 3.49219 1.0918C2.65997 1.20373 2.15799 1.41623 1.78711 1.78711C1.41623 2.15799 1.20373 2.65997 1.0918 3.49219C0.977987 4.33869 0.976562 5.45139 0.976562 7C0.976562 8.5486 0.977987 9.6613 1.0918 10.5078C1.20373 11.34 1.41623 11.842 1.78711 12.2129C2.15799 12.5838 2.65997 12.7963 3.49219 12.9082C4.33869 13.022 5.45139 13.0234 7 13.0234C8.5486 13.0234 9.6613 13.022 10.5078 12.9082C11.34 12.7963 11.842 12.5838 12.2129 12.2129C12.5838 11.842 12.7963 11.34 12.9082 10.5078C13.022 9.6613 13.0234 8.5486 13.0234 7C13.0234 5.45139 13.022 4.33869 12.9082 3.49219C12.7963 2.65997 12.5838 2.15799 12.2129 1.78711C11.842 1.41623 11.34 1.20373 10.5078 1.0918C9.6613 0.977987 8.5486 0.976562 7 0.976562ZM9.60449 9.76758C9.87417 9.76758 10.0927 9.98619 10.0928 10.2559C10.0928 10.5256 9.8742 10.7441 9.60449 10.7441H4.39551C4.12579 10.7441 3.90723 10.5256 3.90723 10.2559C3.90728 9.98619 4.12582 9.76758 4.39551 9.76758H9.60449ZM7 3.25586C7.26966 3.25588 7.48823 3.47448 7.48828 3.74414V7.12305L8.6084 6.00391C8.79912 5.81318 9.1081 5.81318 9.29883 6.00391C9.48935 6.19465 9.48949 6.50368 9.29883 6.69434L7.3457 8.64746C7.25416 8.73907 7.1295 8.79101 7 8.79102C6.87048 8.79102 6.74585 8.73908 6.6543 8.64746L4.70117 6.69434C4.5105 6.50366 4.51061 6.19464 4.70117 6.00391C4.89189 5.81318 5.20088 5.81318 5.3916 6.00391L6.51172 7.12305V3.74414C6.51177 3.47446 6.73032 3.25586 7 3.25586Z" fill="#5D4037" />
                        </svg>
                        Download Outlets
                    </button> */}
                    </div>
                </div>

                {/* Locations Grid - 6 columns layout */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-3">
                    {locationsList.map((loc, idx) => {
                        const isActive = loc === selectedLocation;
                        return (
                            <div
                                key={`${loc}-${idx}`}
                                onClick={() => handleLocationClick(loc)}
                                className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-150 cursor-pointer select-none ${isActive
                                    ? 'go_erp-bg text-white'
                                    : 'bg-[#F4E6E2] text-[#5D4037] hover:bg-[#EAD4CE]'
                                    }`}
                            >
                                <span>{loc}</span>
                                {/* Circular Plus/Target SVG Icon */}
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="6" stroke={isActive ? "white" : "#5D4037"} strokeWidth="1.2" />
                                    <path d="M8 5v6M5 8h6" stroke={isActive ? "white" : "#5D4037"} strokeWidth="1.2" strokeLinecap="round" />
                                </svg>
                            </div>
                        );
                    })}
                    {locationsList.length === 0 && (
                        <div className="col-span-full py-8 text-center text-xs font-medium text-slate-400">
                            No warehouses or distributors defined for this zone. Click Add to create one.
                        </div>
                    )}
                </div>
            </div>

            {/* Sub-Distributors Section matching the picture-perfect layout of mockup */}
            {selectedLocation && (
                <div className="bg-white rounded-3xl shadow-sm border border-[#ECECF2] px-4 py-3 mt-6 ">
                    {/* Header Row */}
                    <div className="flex items-center justify-between">
                        <h6 className="text-base font-bold text-[#1F1F1F] m-0 select-none">
                            {selectedLocation}
                        </h6>
                        {/* <button
                            onClick={handleDownloadLocationDistributors}
                            className="flex items-center gap-1 text-sm font-medium text-[#5D4037] transition-colors cursor-pointer"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className='w-4 h-4' viewBox="0 0 14 14" fill="none">
                                <path d="M7.03711 0C8.54025 -6.51163e-06 9.71858 0.000452274 10.6377 0.124023C11.5783 0.250513 12.3207 0.514056 12.9033 1.09668C13.486 1.67934 13.7504 2.42157 13.877 3.3623C14.0005 4.28141 14 5.45977 14 6.96289V7.03711C14 8.54025 14.0005 9.71858 13.877 10.6377C13.7504 11.5784 13.486 12.3207 12.9033 12.9033C12.3207 13.486 11.5784 13.7504 10.6377 13.877C9.71858 14.0005 8.54025 14 7.03711 14H6.96289C5.45977 14 4.28141 14.0005 3.3623 13.877C2.42157 13.7504 1.67934 13.486 1.09668 12.9033C0.514056 12.3207 0.250513 11.5783 0.124023 10.6377C0.000452274 9.71858 -6.51163e-06 8.54025 0 7.03711V6.96289C-6.51163e-06 5.45977 0.000452274 4.28141 0.124023 3.3623C0.250514 2.42162 0.514048 1.67931 1.09668 1.09668C1.67931 0.514047 2.42163 0.250514 3.3623 0.124023C4.28141 0.000452274 5.45977 -6.51163e-06 6.96289 0H7.03711ZM7 0.976562C5.45139 0.976562 4.33869 0.977987 3.49219 1.0918C2.65997 1.20373 2.15799 1.41623 1.78711 1.78711C1.41623 2.15799 1.20373 2.65997 1.0918 3.49219C0.977987 4.33869 0.976562 5.45139 0.976562 7C0.976562 8.5486 0.977987 9.6613 1.0918 10.5078C1.20373 11.34 1.41623 11.842 1.78711 12.2129C2.15799 12.5838 2.65997 12.7963 3.49219 12.9082C4.33869 13.022 5.45139 13.0234 7 13.0234C8.5486 13.0234 9.6613 13.022 10.5078 12.9082C11.34 12.7963 11.842 12.5838 12.2129 12.2129C12.5838 11.842 12.7963 11.34 12.9082 10.5078C13.022 9.6613 13.0234 8.5486 13.0234 7C13.0234 5.45139 13.022 4.33869 12.9082 3.49219C12.7963 2.65997 12.5838 2.15799 12.2129 1.78711C11.842 1.41623 11.34 1.20373 10.5078 1.0918C9.6613 0.977987 8.5486 0.976562 7 0.976562ZM9.60449 9.76758C9.87417 9.76758 10.0927 9.98619 10.0928 10.2559C10.0928 10.5256 9.8742 10.7441 9.60449 10.7441H4.39551C4.12579 10.7441 3.90723 10.5256 3.90723 10.2559C3.90728 9.98619 4.12582 9.76758 4.39551 9.76758H9.60449ZM7 3.25586C7.26966 3.25588 7.48823 3.47448 7.48828 3.74414V7.12305L8.6084 6.00391C8.79912 5.81318 9.1081 5.81318 9.29883 6.00391C9.48935 6.19465 9.48949 6.50368 9.29883 6.69434L7.3457 8.64746C7.25416 8.73907 7.1295 8.79101 7 8.79102C6.87048 8.79102 6.74585 8.73908 6.6543 8.64746L4.70117 6.69434C4.5105 6.50366 4.51061 6.19464 4.70117 6.00391C4.89189 5.81318 5.20088 5.81318 5.3916 6.00391L6.51172 7.12305V3.74414C6.51177 3.47446 6.73032 3.25586 7 3.25586Z" fill="#5D4037" />
                            </svg>
                            Download Outlets
                        </button> */}
                    </div>

                    {/* Table / List Header Border */}
                    <div className="border-t border-[#828282] my-1" />

                    {/* Distributors Rows */}
                    <div className="divide-y divide-[#ECECF2]">
                        {activeLocDistributors.map((dist) => (
                            <div
                                key={dist.code}
                                className="flex flex-col md:flex-row md:items-center md:justify-between py-3 gap-4"
                            >
                                {/* Left Side: Code and Name */}
                                <div className="flex items-center gap-8">
                                    <span className="text-sm text-[#1F1F1F] font-regular tracking-wide">
                                        {dist.code}
                                    </span>
                                    <span className="text-sm text-[#1F1F1F] font-regular">
                                        {dist.name}
                                    </span>
                                </div>

                                {/* Right Side: Actions Buttons */}
                                <div className="flex items-center gap-3">
                                    {/* View Distributor */}
                                    <button
                                        onClick={() => handleViewDistributor(dist)}
                                        className="px-4 py-2 bg-[#5D4037] text-white rounded-full text-xs font-medium transition-all cursor-pointer"
                                    >
                                        View Distributor
                                    </button>

                                    {/* Edit Distributor */}
                                    {/* <button
                                        onClick={() => handleOpenEditDistributor(dist)}
                                        className="px-4 py-2 bg-[#5D4037] text-white rounded-full text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className='w-4 h-4' viewBox="0 0 14 14" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M9.35157 10.3404C9.70292 10.3405 9.98807 10.6247 9.98829 10.9762C9.98829 11.3277 9.70306 11.6128 9.35157 11.6129H3.01368C2.6621 11.6129 2.37696 11.3277 2.37696 10.9762C2.37718 10.6247 2.66223 10.3404 3.01368 10.3404H9.35157ZM11.0908 6.99764C11.0908 6.64598 11.376 6.36092 11.7275 6.36092C12.079 6.36111 12.3643 6.6461 12.3643 6.99764V10.1793L12.3594 10.3756C12.2614 12.3897 10.5998 13.9944 8.56056 13.9996L8.5586 13.2369V13.3639L8.56056 13.9996H3.80372L3.80567 13.3639V13.0035L3.80372 13.9996C1.69846 13.9945 -0.00431738 12.2845 8.22306e-06 10.1793V5.40584C-0.0037622 3.30106 1.69881 1.59158 3.80372 1.58651H6.18165C6.53322 1.58651 6.81837 1.87164 6.81837 2.22323C6.81812 2.57461 6.53307 2.85897 6.18165 2.85897H3.80665C2.40439 2.86242 1.27044 4.00262 1.27345 5.40487V10.1822L0.636727 10.1803H0.424813L1.27345 10.1822C1.27069 11.5843 2.40454 12.7237 3.80665 12.7271H8.55665C9.95881 12.7237 11.0935 11.5843 11.0908 10.1822L11.9395 10.1803H11.7275L11.0908 10.1822V6.99764ZM9.53419 0.519125C9.93723 0.0123213 10.6353 -0.141598 11.209 0.138266L11.3242 0.201743L11.3887 0.246664L12.7178 1.30526L12.7578 1.33944C13.0144 1.58109 13.159 1.9192 13.1572 2.27206C13.1555 2.59966 13.0258 2.91154 12.7998 3.14608L11.794 4.3492V4.35018L8.35645 8.44881L8.34864 8.45955C8.20161 8.62771 8.0052 8.74559 7.78712 8.79745L7.78028 8.7994L5.99805 9.2037C5.96252 9.21173 5.92604 9.2165 5.88966 9.21834C5.32436 9.2465 4.83186 8.83529 4.7588 8.27401C4.75529 8.24694 4.75294 8.21927 4.75294 8.19198V6.35995C4.75296 6.34573 4.75392 6.33116 4.75489 6.31698C4.77072 6.08617 4.86314 5.86654 5.01856 5.69491L8.38673 1.88729L9.53321 0.517172L9.53419 0.519125ZM6.02638 6.47811V7.8912L7.4297 7.57284L10.0547 4.44198C9.76046 4.32146 9.50152 4.14953 9.28321 3.95956C9.08099 3.78359 8.90605 3.58663 8.75782 3.38827L6.02638 6.47811ZM10.6455 1.28182C10.6041 1.2647 10.5552 1.27566 10.5274 1.31209C10.5216 1.31965 10.5159 1.32727 10.5098 1.33456L9.62306 2.39217C9.74704 2.59731 9.91397 2.82106 10.1191 2.99959C10.3799 3.22636 10.6618 3.35355 10.9775 3.33944L11.8272 2.32381C11.844 2.30368 11.8627 2.28507 11.8818 2.26717L10.6455 1.28182Z" fill="white" />
                                        </svg>
                                        Edit Distributor
                                    </button> */}

                                    {/* Add Area */}
                                    {/* <button
                                        onClick={() => handleOpenAddArea(dist)}
                                        className="px-4 py-2 bg-[#5D4037] text-white rounded-full text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className='w-4 h-4' viewBox="0 0 14 14" fill="none">
                                            <path d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13412 13.9999 5.18208e-06 10.8659 0 7C3.22359e-06 3.13409 3.13412 0.000129983 7 0ZM7 0.976562C3.67356 0.976692 0.976566 3.67353 0.976562 7C0.976568 10.3265 3.67356 13.0233 7 13.0234C10.3265 13.0234 13.0234 10.3265 13.0234 7C13.0234 3.67345 10.3265 0.976562 7 0.976562ZM7 4.55859C7.26972 4.55859 7.48828 4.77715 7.48828 5.04688V6.51172H8.9541C9.22364 6.51194 9.44238 6.73041 9.44238 7C9.44236 7.26957 9.22362 7.48806 8.9541 7.48828H7.48828V8.95312C7.48828 9.22285 7.26972 9.44141 7 9.44141C6.73038 9.44129 6.51172 9.22278 6.51172 8.95312V7.48828H5.04688C4.77717 7.48828 4.55861 7.2697 4.55859 7C4.55859 6.73028 4.77715 6.51172 5.04688 6.51172H6.51172V5.04688C6.51172 4.77722 6.73038 4.55871 7 4.55859Z" fill="white" />
                                        </svg>
                                        Add Area
                                    </button> */}
                                </div>
                            </div>
                        ))}
                        {activeLocDistributors.length === 0 && (
                            <div className="py-8 text-center text-xs font-medium text-slate-400">
                                No distributors found under this location.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MODAL: Edit Zone Name */}
            {isEditZoneOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <h5 className="text-sm font-bold text-slate-800">Edit Zone Name</h5>
                            <button
                                onClick={() => setIsEditZoneOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleEditZoneSubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Zone Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Mumbai Metro"
                                    value={tempZoneName}
                                    onChange={(e) => setTempZoneName(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5D4037]/20 focus:border-[#5D4037] outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsEditZoneOpen(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-50 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-[#5D4037] hover:bg-[#4E342E] text-white font-bold text-xs rounded-lg cursor-pointer"
                                >
                                    Rename Zone
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: Add Warehouse / Distributor (Location pill) */}
            {isAddLocationOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <h5 className="text-sm font-bold text-slate-800">Add New {locationType}</h5>
                            <button
                                onClick={() => setIsAddLocationOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleAddLocationSubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    {locationType} Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder={`e.g. New {locationType}`}
                                    value={newLocationName}
                                    onChange={(e) => setNewLocationName(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5D4037]/20 focus:border-[#5D4037] outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAddLocationOpen(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-50 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-[#5D4037] hover:bg-[#4E342E] text-white font-bold text-xs rounded-lg cursor-pointer"
                                >
                                    Create {locationType}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: View Distributor details */}
            {isViewDistributorOpen && selectedDistributor && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                            <h5 className="text-sm font-bold text-slate-800">Distributor Information</h5>
                            <button
                                onClick={() => setIsViewDistributorOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div className="grid grid-cols-3">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Code</span>
                                <span className="col-span-2 text-slate-800 font-medium">{selectedDistributor.code}</span>
                            </div>
                            <div className="grid grid-cols-3">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Name</span>
                                <span className="col-span-2 text-slate-800 font-bold">{selectedDistributor.name}</span>
                            </div>
                            <div className="grid grid-cols-3">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Location</span>
                                <span className="col-span-2 text-slate-800 font-medium">{selectedLocation}</span>
                            </div>
                            <div className="grid grid-cols-3">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Zone</span>
                                <span className="col-span-2 text-slate-800 font-medium">{activeZone}</span>
                            </div>
                            <div className="pt-2 border-t border-slate-100">
                                <span className="font-bold text-slate-400 uppercase tracking-wider text-[10px] block mb-1">
                                    Assigned Beats / Areas
                                </span>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {(selectedDistributor.areas || []).map((area, index) => (
                                        <span
                                            key={`${area}-${index}`}
                                            className="px-2 py-1 bg-[#F4E6E2] text-[#5D4037] text-[10px] font-semibold rounded-md"
                                        >
                                            {area}
                                        </span>
                                    ))}
                                    {(!selectedDistributor.areas || selectedDistributor.areas.length === 0) && (
                                        <span className="text-slate-400 italic font-medium">No areas assigned yet.</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end pt-3">
                            <button
                                onClick={() => setIsViewDistributorOpen(false)}
                                className="px-5 py-2 bg-[#5D4037] text-white font-bold text-xs rounded-lg cursor-pointer hover:bg-[#4E342E]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Edit Distributor */}
            {isEditDistributorOpen && selectedDistributor && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <h5 className="text-sm font-bold text-slate-800">Edit Distributor</h5>
                            <button
                                onClick={() => setIsEditDistributorOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleEditDistributorSubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Distributor Code
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. 6000888"
                                    value={editDistCode}
                                    onChange={(e) => setEditDistCode(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5D4037]/20 focus:border-[#5D4037] outline-none"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Distributor Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Maurya Enterprises"
                                    value={editDistName}
                                    onChange={(e) => setEditDistName(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5D4037]/20 focus:border-[#5D4037] outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsEditDistributorOpen(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-50 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-[#5D4037] hover:bg-[#4E342E] text-white font-bold text-xs rounded-lg cursor-pointer"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: Add Beat Area */}
            {isAddAreaOpen && selectedDistributor && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl border border-slate-100 space-y-4">
                        <div className="flex items-center justify-between">
                            <h5 className="text-sm font-bold text-slate-800">Add Beat / Area</h5>
                            <button
                                onClick={() => setIsAddAreaOpen(false)}
                                className="text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleAddAreaSubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Area / Route Name
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Malad Linking Road Beat"
                                    value={newAreaName}
                                    onChange={(e) => setNewAreaName(e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5D4037]/20 focus:border-[#5D4037] outline-none"
                                />
                            </div>

                            <div className="flex justify-end gap-2.5 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsAddAreaOpen(false)}
                                    className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-50 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-[#5D4037] hover:bg-[#4E342E] text-white font-bold text-xs rounded-lg cursor-pointer"
                                >
                                    Add Area
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};
